import { useEffect, useState } from 'react';
import Loading from "components/Loading";
import { useTranslation } from "react-i18next"
import { PieChart } from '@mui/x-charts/PieChart';
import { getQrStats } from 'api/qr';

interface BySource { qr: number; link: number; phone: number; }

interface QrStat {
    id: number;
    slug: string;
    name: string;
    is_active: boolean;
    total: number;
    by_source: BySource;
}

interface StatsResponse {
    summary: { total: number; by_source: BySource };
    detail: QrStat[];
}

const Dashboard = () => {
    const { ready, t } = useTranslation();
    const [data, setData] = useState<StatsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const fetchStats = (from: string, to: string) => {
        setLoading(true);
        getQrStats(from || undefined, to || undefined)
            .then(r => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchStats('', ''); }, []);

    if (!ready) return <Loading />;

    const pieData = data ? [
        { id: 0, value: data.summary.by_source.qr, label: 'QR', color: '#4e79a7' },
        { id: 1, value: data.summary.by_source.link, label: 'Link directo', color: '#f28e2b' },
        { id: 2, value: data.summary.by_source.phone, label: 'Teléfono', color: '#59a14f' },
    ].filter(d => d.value > 0) : [];

    return (
        <>
            <div className="card mb-4">
                <div className="card-header">
                    <h3><i className="fa fa-home"></i> Home</h3>
                </div>
                <div className="card-body"><p>{t("welcome")}</p></div>
            </div>

            <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0"><i className="fa fa-chart-pie mr-2"></i>Visitas por fuente</h5>
                    <div className="d-flex align-items-center">
                        <input
                            type="date"
                            className="form-control form-control-sm mr-2"
                            style={{ width: 145 }}
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                        />
                        <span className="mr-2 text-muted">—</span>
                        <input
                            type="date"
                            className="form-control form-control-sm mr-2"
                            style={{ width: 145 }}
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                        />
                        <button
                            className="btn btn-sm btn-primary"
                            onClick={() => fetchStats(dateFrom, dateTo)}
                        >
                            Filtrar
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    {loading ? (
                        <Loading />
                    ) : !data || data.summary.total === 0 ? (
                        <p className="text-muted mb-0 text-center">No hay visitas en el período seleccionado.</p>
                    ) : (
                        <div className="d-flex align-items-center justify-content-center flex-wrap">
                            <PieChart
                                series={[{
                                    data: pieData,
                                    highlightScope: { faded: 'global', highlighted: 'item' },
                                    innerRadius: 60,
                                    outerRadius: 120,
                                    paddingAngle: 3,
                                    cornerRadius: 4,
                                    arcLabel: () => '',
                                    arcLabelMinAngle: 360,
                                }]}
                                width={400}
                                height={260}
                                slotProps={{ legend: { hidden: true } }}
                            />
                            <div className="ml-4">
                                {pieData.map(d => (
                                    <div key={d.id} className="d-flex align-items-center mb-2">
                                        <span style={{ width: 14, height: 14, borderRadius: 3, background: d.color, display: 'inline-block', marginRight: 8 }}></span>
                                        <span>{d.label}: <strong>{d.value}</strong></span>
                                    </div>
                                ))}
                                <div className="mt-3 text-muted">Total: <strong>{data.summary.total}</strong></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h5 className="mb-0"><i className="fa fa-qrcode mr-2"></i>Detalle por QR</h5>
                </div>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="p-3"><Loading /></div>
                    ) : !data || data.detail.length === 0 ? (
                        <p className="p-3 text-muted mb-0">No hay datos.</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="thead-light">
                                    <tr>
                                        <th>Nombre</th>
                                        <th>Slug</th>
                                        <th>Estado</th>
                                        <th className="text-center">QR</th>
                                        <th className="text-center">Link directo</th>
                                        <th className="text-center">Teléfono</th>
                                        <th className="text-center">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.detail.map(s => (
                                        <tr key={s.id}>
                                            <td>{s.name || <span className="text-muted">—</span>}</td>
                                            <td><code>{s.slug}</code></td>
                                            <td>
                                                <span className={`badge badge-${s.is_active ? 'success' : 'secondary'}`}>
                                                    {s.is_active ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td className="text-center">{s.by_source.qr}</td>
                                            <td className="text-center">{s.by_source.link}</td>
                                            <td className="text-center">{s.by_source.phone}</td>
                                            <td className="text-center"><strong>{s.total}</strong></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default Dashboard;
